/* @jsxImportSource react */
/* Vite's react plugin already compiles JSX with react/jsx-runtime; the pragma keeps
   that true under Playwright's babel transform too, so the node-side unit tests
   can render these components with react-dom/server. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { admins } from '../api/client';
import type { AdminsView } from '../api/types';
import { ErrorState, Spinner } from '../components/ui';

/**
 * Form validation, pure so the unit tests pin it: an allowlist entry has to be
 * a plausible e-mail (the same rule the API applies), and a duplicate is
 * reported instead of silently no-oping.
 */
export function validateAdminEmail(value: string, existing: readonly string[]): string | null {
  const email = value.trim().toLowerCase();
  if (email === '') return 'Укажите e-mail.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Это не похоже на e-mail (пример: name@ismart.org).';
  if (existing.includes(email)) return 'Такой адрес уже в списке.';
  return null;
}

/** Add form + the audit note (every change lands in `bm:audit`). */
export function AdminAddForm({ admins: known, you }: { admins: string[]; you: string }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const client = useQueryClient();

  const add = useMutation({
    mutationFn: (value: string) => admins.add(value),
    onSuccess: () => {
      setEmail('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['admins'] });
    },
    onError: (mutationError) => setError(String(mutationError instanceof Error ? mutationError.message : mutationError)),
  });

  function submit(): void {
    const problem = validateAdminEmail(email, known);
    if (problem) {
      setError(problem);
      return;
    }
    add.mutate(email.trim());
  }

  return (
    <form
      className="toolbar"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="toolbar__field" style={{ flex: '1 1 260px' }}>
        <label className="toolbar__label" htmlFor="admin-email">
          E-mail администратора
        </label>
        <input
          id="admin-email"
          className="input"
          type="email"
          autoComplete="off"
          placeholder="name@ismart.org"
          value={email}
          aria-invalid={error ? 'true' : undefined}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
        />
        {error ? (
          <p className="field__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <button type="submit" className="btn btn--primary" disabled={add.isPending}>
        Добавить
      </button>
      <p className="field__hint" style={{ flexBasis: '100%', margin: 0 }}>
        Изменения пишутся в аудит (<code>bm:audit</code>) и действуют со следующего запроса: сессия не обязана
        перелогиниваться. Вы вошли как <code>{you}</code> — удалить свой адрес нельзя.
      </p>
    </form>
  );
}

/** `/admins` — the CTO-requested allowlist form. */
export function AdminsPage() {
  const query = useQuery({ queryKey: ['admins'], queryFn: ({ signal }) => admins.list(signal) });
  const client = useQueryClient();
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (email: string) => admins.remove(email),
    onSuccess: () => {
      setRemoveError(null);
      void client.invalidateQueries({ queryKey: ['admins'] });
    },
    onError: (mutationError) => setRemoveError(String(mutationError instanceof Error ? mutationError.message : mutationError)),
  });

  if (query.isPending) return <Spinner label="Загружаю список админов" />;
  if (query.isError) return <ErrorState message={String(query.error)} onRetry={() => void query.refetch()} />;

  const data = query.data as AdminsView;
  return (
    <>
      <div className="page-header">
        <div>
          <h1>Админы</h1>
          <p className="page-header__sub">Allowlist входа (Valkey <code>bm:allowlist</code>) — сеется из ALLOWED_EMAILS при первом старте.</p>
        </div>
      </div>

      <section className="card">
        <AdminAddForm admins={data.admins} you={data.you} />
      </section>

      {removeError ? (
        <p className="field__error" role="alert">
          {removeError}
        </p>
      ) : null}

      <section className="card card--flush">
        <div className="table-wrap">
          <table className="data" data-testid="admins-table">
            <thead>
              <tr>
                <th>E-mail</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.admins.map((email) => {
                const self = email === data.you;
                return (
                  <tr key={email}>
                    <td className="cell-primary">
                      {email}
                      {self ? <span className="cell-muted"> (вы)</span> : null}
                    </td>
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={self}
                        title={self ? 'Свой адрес удалить нельзя' : 'Убрать из allowlist'}
                        onClick={() => remove.mutate(email)}
                      >
                        Убрать
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
